import { PayableTimeCap } from '@scale/shared/types/queue';
import { MultiTurnInteractionConfiguration } from '@scale/shared/types/chat/params';
import _ from 'lodash';

function getMinTurns(config: MultiTurnInteractionConfiguration) {
  return _.isNil(config?.minTurns) || _.isString(config?.minTurns) ? 0 : config.minTurns;
}

export function numberToDollars(amount: number) {
  if (amount % 1 !== 0) {
    return `$${amount.toFixed(2)}`;
  } else {
    return `$${amount}`;
  }
}

/**
 * Get the maximum payable time (in minutes) for a task,
 * based also on the number of turns and the additional minutes per turn.
 */
export function getMaxPayableTime(
  config: MultiTurnInteractionConfiguration,
  numOfTurns: number | undefined,
  payableTime: PayableTimeCap | undefined,
): number {
  const minTurns: number = getMinTurns(config);
  const turns = Math.max(minTurns ?? 1, numOfTurns ?? 1);
  const additionalTurns = Math.max(0, turns - 1);

  const additionalMinutesPerTurn = payableTime?.additionalMinutesPerTurn ?? 0;
  const maxPayableMinutes = payableTime?.maxPayableMinutes ?? 0;

  return maxPayableMinutes + additionalMinutesPerTurn * additionalTurns;
}

/**
 * Helper function to calculate warning threshold time in minutes based
 * off the initial available time, to show user a toast alert.
 *
 * - 20% of the initial time, rounded up to the nearest minute
 * - no less than 1 minute, up to 5 minutes
 * - if initial time is less than 1 minute, don't show warning
 *
 * @param initialTimeMinutes - The initial available time in minutes
 * @returns The warning threshold in minutes, or null if the initial time is less than 1 minute
 */
export function getRemainingTimeWarningThresholdMinutes(initialTimeMinutes: number): number | null {
  if (initialTimeMinutes <= 1) {
    // don't show warning
    return null;
  }
  // 20% rounded up; minimum 1 minute, maximum 5 minutes
  const threshold = Math.ceil(initialTimeMinutes * 0.2);
  return Math.min(Math.max(threshold, 1), 5);
}
